import {
  CHANGE_PASSWORD_FORM_ID
} from "./chunk-Y6C2WA2G.mjs";

// src/internal/components/change-password-panel/change-password-form.tsx
import * as React from "react";
import { Controller, useForm } from "react-hook-form";
import { object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box } from "@mui/material";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { useAuthContext } from "@clara/auth";
import { useT } from "@clara/locale";
import {
  isValidPassword,
  PasswordInput,
  PasswordInputField,
  PasswordInputRequirements,
  Banner
} from "@clara/ui";
import { parseResponse } from "@clara/utils";
import { jsx, jsxs } from "react/jsx-runtime";
var ChangePasswordSchema = object().shape({
  currentPassword: string().test((value, context) => {
    const v = context.parent.version;
    return v === "v3" ? true : !!value;
  }),
  newPssw: string().test("newPssw", "", (value) => {
    return !!(value && isValidPassword(value));
  }).required("fields.required.error"),
  confirm_password: string().required("fields.required.error").test("password-match", "fields.confirmPassword.error", function(value) {
    return this.parent.newPssw === value;
  })
});
var errors = {
  417: { B030: "error.reused.pssw", B031: "error.currentPsswd.mismatch" }
};
var ChangePasswordForm = (props) => {
  const { error, loading, onSubmit, ...rest } = props;
  const {
    setPanelFormData,
    config: { version }
  } = useAuthContext();
  const form = useForm({
    defaultValues: {
      newPssw: "",
      confirm_password: "",
      currentPassword: "",
      version
    },
    resolver: yupResolver(ChangePasswordSchema),
    mode: "onChange"
  });
  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
    trigger
  } = form;
  const { t } = useT(["auth", "common"]);
  const password = watch("newPssw");
  const confirmPsswd = watch("confirm_password");
  const currPsswd = watch("currentPassword");
  const [errorCode, setErrorCode] = React.useState(
    null
  );
  React.useEffect(() => {
    setErrorCode(null);
  }, [currPsswd, confirmPsswd, password]);
  React.useEffect(() => {
    if (!!error) {
      parseResponse(error.response).then((e) => {
        setErrorCode(e);
      });
    }
  }, [error]);
  const errorMsgs = React.useMemo(() => {
    var _a;
    return !!error && !!errorCode && (errors == null ? void 0 : errors[error.response.status]) ? (_a = errors == null ? void 0 : errors[error.response.status]) == null ? void 0 : _a[errorCode.code] : "twofactor.totp.error";
  }, [error, errorCode]);
  React.useEffect(() => {
    setPanelFormData({
      formId: CHANGE_PASSWORD_FORM_ID,
      payload: {
        isValid,
        loading
      }
    });
  }, [isValid, loading, setPanelFormData]);
  return /* @__PURE__ */ jsxs(
    Stack,
    {
      id: CHANGE_PASSWORD_FORM_ID,
      component: "form",
      onSubmit: handleSubmit(onSubmit),
      spacing: 2,
      width: "100%",
      ...rest,
      children: [
        version !== "v3" && /* @__PURE__ */ jsx(
          Controller,
          {
            control,
            name: "currentPassword",
            render: ({
              field: { ref: inputRef, ...field },
              fieldState: { error: error2 }
            }) => /* @__PURE__ */ jsx(
              PasswordInput,
              {
                ...field,
                inputRef,
                error: !!error2,
                helperText: t(error2 == null ? void 0 : error2.message),
                label: t(`fields.${field.name}.label`),
                size: "small",
                variant: "outlined",
                children: /* @__PURE__ */ jsx(PasswordInputField, { className: "fs-exclude" })
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          Controller,
          {
            control,
            name: "newPssw",
            render: ({
              field: { ref: inputRef, onChange, ...field },
              fieldState: { error: error2 }
            }) => /* @__PURE__ */ jsx(
              PasswordInput,
              {
                ...field,
                inputRef,
                error: !!error2,
                onChange: (e) => {
                  onChange(e.target.value);
                  trigger("confirm_password");
                },
                helperText: t(error2 == null ? void 0 : error2.message),
                label: t(`fields.${field.name}.label`),
                size: "small",
                variant: "outlined",
                children: /* @__PURE__ */ jsx(PasswordInputField, { className: "fs-exclude" })
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          Controller,
          {
            control,
            name: "confirm_password",
            render: ({
              field: { ref: inputRef, ...field },
              fieldState: { error: error2 }
            }) => /* @__PURE__ */ jsx(
              PasswordInput,
              {
                ...field,
                inputRef,
                error: !!error2,
                helperText: t(error2 == null ? void 0 : error2.message),
                label: t("fields.confirm.newPssw.label"),
                size: "small",
                variant: "outlined",
                children: /* @__PURE__ */ jsx(PasswordInputField, { className: "fs-exclude" })
              }
            )
          }
        ),
        /* @__PURE__ */ jsx(
          Box,
          {
            sx: (theme) => ({
              border: `${theme.spacing(0.125)} solid ${theme.palette.grey[200]}`,
              borderRadius: theme.spacing(1),
              padding: `${theme.spacing(1)} ${theme.spacing(3)}`,
              backgroundColor: theme.palette.grey[25],
              "& p": {
                fontSize: theme.spacing(1.5),
                marginBottom: "6px"
                // color: theme.palette.tokens.gray40,
              },
              "& span": {
                fontSize: theme.spacing(1.5),
                lineHeight: theme.spacing(2),
                paddingLeft: theme.spacing(1),
                marginLeft: `0px !important`,
                color: theme.palette.tokens.gray30
              }
            }),
            children: /* @__PURE__ */ jsxs(PasswordInput, { value: password, children: [
              /* @__PURE__ */ jsx(Typography, { variant: "body1", textAlign: "left", children: t("fields.password.req.helper") }),
              /* @__PURE__ */ jsx(
                PasswordInputRequirements,
                {
                  mapRequirement: ([rule, valid]) => [
                    t(`fields.password.req.${rule}`),
                    valid
                  ]
                }
              )
            ] })
          }
        ),
        !!error && !!errorCode && /* @__PURE__ */ jsx(Banner, { status: "critical", children: t(errorMsgs) })
      ]
    }
  );
};

export {
  ChangePasswordSchema,
  ChangePasswordForm
};
